import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GTMService } from '@services';
import { EventLogGroups, EventsStore, EventTypes, InstallationDetailsStore } from '@store';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { CustomTranslationsService } from '../../../../operational-data-common/translations';

@Component({
  selector: 'app-event-log',
  templateUrl: './event-log.component.html',
  styleUrls: ['./event-log.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventLogComponent implements OnInit, OnDestroy {
  maxDate = new Date();
  minDate: Date;
  eventDateControl = new FormControl<Date>(undefined);
  eventFiltersForm = new FormControl<string[] | undefined>(undefined);

  eventFilterAvailableOptions: Number = 0;

  readonly eventLogGroups = EventLogGroups;

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(map((result) => result.matches));

  panelContentHeight: number;

  updateFilters = new Subject<void>();
  filterDate: string;
  filterTypes: string[];

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    protected customTranslationsService: CustomTranslationsService,
    public eventsStore: EventsStore,
    public installationDetailsStore: InstallationDetailsStore,
    private translateService: TranslateService,
    private gtmService: GTMService,
    private breakpointObserver: BreakpointObserver
  ) {}

  viConfirmObject = {
    title: this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DOWNLOAD.MODAL_HEADER'
    ),
    content: this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.EVENT_LOG.DOWNLOAD.MODAL_CONTENT'
    ),
    cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
    confirmBtn: this.translateService.instant('GENERAL.BUTTON.UNDERSTOOD'),
  };

  ngOnInit(): void {
    this.updateFilters.pipe(takeUntil(this.ngUnsubscribe), debounceTime(500)).subscribe(() => {
      this.eventsStore.updateFilters(this.filterTypes, this.filterDate);
    });

    this.installationDetailsStore.getSelectedGateway$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((gateway) => {
        this.minDate = new Date(gateway.registeredAt);
      });

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    });

    this.eventFilterAvailableOptions = this.eventLogGroups
      .map((eventsGroup) => eventsGroup.events)
      .map((items) => items.length)
      .reduce((a, b) => a + b, 0);
  }

  filterChanged(): void {
    this.filterTypes = this.getFiltersFlattened(this.eventFiltersForm.value);
    this.extendFilterTypesByDynamicSavingFilter();
    this.updateFilters.next();
    this.gtmService.track('onSelect', 'Event filter', 'eventLog');
  }

  onDateChange(event: { target: { value: string } }): void {
    const date = new Date(event.target.value);
    const isDate = moment(date).isValid();

    this.filterDate = isDate ? date.toISOString() : (this.filterDate = undefined);
    this.updateFilters.next();
    this.gtmService.track('onChange', 'Event date', 'eventLog');
  }

  clearDateFilter(): void {
    this.eventDateControl.reset();
    this.filterDate = undefined;
    this.updateFilters.next();
  }

  ngOnDestroy(): void {
    this.eventsStore.resetSubscriptions();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  trackClose(): void {
    this.gtmService.track('onClose', 'Event Log', 'eventLog');
  }

  private getFiltersFlattened(filters: (string | string[])[]): string[] {
    return Array.prototype.concat(...filters);
  }

  private extendFilterTypesByDynamicSavingFilter(): void {
    if (this.eventFiltersForm.value.includes(EventTypes.DYNAMIC_SAVING_TIPS_HEATINGCURVE_SAVINGS)) {
      const dynamicEnergySavingTips = [
        EventTypes.SETBACK_DURATION_CAN_BE_INCREASED_BY_ONE_HOUR,
        EventTypes.DHW_TARGET_TEMPERATURE_CAN_BE_DECREASED,
        EventTypes.DHW_DURATION_CAN_BE_DECREASED,
      ];
      this.filterTypes = this.filterTypes.concat(this.getFiltersFlattened(dynamicEnergySavingTips));
    }
  }
}
